import { LabelMode } from "./../../../../../utils/label-mode.enum";
import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, signal } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import moment from "moment";
import { debounceTime, forkJoin, Observable } from "rxjs";
import { AuthService } from "src/app/pages/auth";
import { MonitorService } from "src/app/services/api/monitor.service";
import { AxisChartData } from "src/app/services/model/apex-chart.model";
import {
  DatatableStudentActivityHistory,
  StudentDurationLesson,
} from "src/app/services/model/student.model";
import { TableColumn } from "src/app/services/model/tables.model";
import { UtilitiesService } from "src/app/services/utilities-service/utilities.service";
import { SharedModule } from "src/shared/shared.module";
import { DateFormat } from "src/utils/enum/date-format.enum";

@Component({
  selector: "app-study-record",
  imports: [CommonModule, SharedModule, ReactiveFormsModule],
  templateUrl: "./study-record.component.html",
  styleUrl: "./study-record.component.scss",
})
export class StudyRecordComponent implements OnInit {
  #fb = inject(FormBuilder);
  #utilitiesService = inject(UtilitiesService);
  #monitorService = inject(MonitorService);
  #authService = inject(AuthService);
  #route = inject(ActivatedRoute);
  currentUser = this.#authService.currentUser()?.user;
  isStudent = signal<boolean>(this.currentUser?.role.id === 2);
  monitorStudent = this.#monitorService.monitorStudent;

  optionDates = [
    {
      name: "7 วัน",
      value: 7,
    },
    {
      name: "30 วัน",
      value: 30,
    },
    {
      name: "ทั้งหมด",
      value: null,
    },
  ];

  durationInfo = signal<AxisChartData>({ labels: [], series: [] });
  durationChart: AxisChartData = { labels: [], series: [] };

  activitiesSource = signal<DatatableStudentActivityHistory[] | null>(null);
  dataColumns: TableColumn[] = <TableColumn[]>[];

  isLoading = signal(true);
  tableLoading = signal(true);
  firstInitLoading = signal(true);

  form: FormGroup;
  LabelMode = LabelMode;

  ngOnInit(): void {
    this.buildForm();
    this.initColumns();

    // Re-fetch when the parent picks a different child.
    //
    // This tab read the selected student from a service signal ONCE, on init,
    // so switching children rewrote the URL and left the previous child's
    // chart and table on screen — a parent of two was shown one child's study
    // time under the other one's name, with nothing to tell them apart. The
    // quiz history and the skills report next to it both watch the query
    // string; this now does the same. Emits immediately, so the first load
    // still happens here.
    this.#route.queryParamMap.subscribe(() => this.fetchData());
  }

  buildForm() {
    this.form = this.#fb.group({
      back_date: [this.optionDates[0].value],
      days: 30,
      course_id: [null],
      page: [1],
      datatable: [true],
    });

    this.form
      .get("course_id")
      ?.valueChanges.pipe(debounceTime(200))
      .subscribe((val) => {
        this.fetchData();
      });
  }

  initColumns() {
    this.dataColumns = [
      {
        name: "วันที่",
        prop: "created_at",
        headerClass: "min-w-150px fw-semibold",
        propClass: "fw-semibold fs-7",
      },
      {
        name: "คอร์สเรียน",
        prop: "course_name",
        propClass: "text-info text-hover-info  fw-semibold fs-7",
        headerClass: "min-w-150px fw-semibold",
      },

      {
        name: "บทเรียน",
        prop: "chapter_group_name",
        headerClass: "min-w-80px fw-semibold text-center",
        propClass: "fw-normal fs-7",
        cellClass: "justify-content-center",
      },
      {
        name: "หัวข้อ",
        prop: "chapter_name",
        headerClass: "min-w-150px ",
        propClass: "fw-normal fs-7",
      },
      {
        // The API sends seconds. The header said hours, so a 42-minute
        // session printed as "2520" under "(ชม.)".
        name: "เวลารวม",
        prop: "duration_label",
        headerClass: "min-w-100px fw-semibold text-center",
        propClass: "fw-normal fs-7",
        cellClass: "justify-content-center ",
      },
    ];
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  filterDate(value: number | null) {
    this.form.patchValue({ back_date: value });
    this.fetchData();
  }

  async fetchData() {
    const params = this.form.getRawValue();
    this.durationInfo.set({ labels: [], series: [] });
    this.isLoading.update(() => true);
    this.tableLoading.update(() => true);
    forkJoin([
      this.#monitorService.getDurationLessons(params) as Observable<any>,
      this.#monitorService.getActivityHistory(params) as Observable<any>,
    ]).subscribe({
      next: ([durations, activities_history]) => {
        const results = this.durationInfo();
        results?.series.push(
          {
            name: "เวลาที่เรียนรวม",
            data: [] as number[],
          },
          {
            name: "เวลาเรียนต่อเนื่องสูงสุด",
            data: [] as number[],
          },
          {
            name: "เวลาเรียนต่อเนื่องต่ำสุด",
            data: [] as number[],
          }
        );

        // Oldest first. The API returns newest-first, so the x-axis ran
        // backwards under a heading that says "ย้อนหลัง 7 วัน" — time reading
        // right-to-left in a chart nobody expects to.
        [...durations].reverse().map((item: StudentDurationLesson, index: number) => {
          results?.labels!.push(
            moment(new Date(item.created_at))
              .add(543, "year")
              .format("DD/MM/YYYY")
          );
          (results?.series[0].data as number[]).push(
            this.getTime(item.total_duration)
          );
          (results?.series[1].data as number[]).push(this.getTime(item.max));
          (results?.series[2].data as number[]).push(this.getTime(item.min));
        });

        activities_history.data.map((item: any) => {
          // The API sends Thai wall-clock time with a `Z` on the end — the
          // instant is right, the label is a lie. Parsed as UTC it came back
          // seven hours later, which put the newest session on tomorrow's
          // date, one row below a chart (built from date-only strings) that
          // had it right. Dropping the `Z` parses it as local, which is what
          // it always was.
          item.created_at = this.#utilitiesService.dateFormat(
            typeof item.created_at === "string"
              ? item.created_at.replace(/Z$/, "")
              : item.created_at,
            DateFormat.DATE_TIME
          );
          item.duration_label = this.durationLabel(item.duration_time);
        });
        activities_history.page = 1;

        this.durationInfo.set(results);
        this.activitiesSource.update(() => activities_history);
      },
      error: (error) => {
        this.tableLoading.update((_) => false);
        this.firstInitLoading.update(() => false);
        this._setLoading(false);
      },
      complete: () => {
        this.tableLoading.update((_) => false);
        this.firstInitLoading.update(() => false);
        this._setLoading(false);
      },
    });
  }

  onTableChange(event: any) {}

  getTime(time: number): number {
    return Number((time / 3600).toFixed(2));
  }

  /** Seconds as something a parent reads at a glance: "1 ชม. 12 นาที",
   *  "42 นาที", "40 วินาที" — never a bare four-digit count of seconds. */
  durationLabel(seconds: number | null | undefined): string {
    const total = Math.max(0, Math.round(Number(seconds) || 0));
    if (total < 60) return `${total} วินาที`;
    // Round to whole minutes first, then split: rounding only the remainder
    // turned 59m59s into "60 นาที" instead of "1 ชม.".
    const totalMinutes = Math.round(total / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (!hours) return `${minutes} นาที`;
    return minutes ? `${hours} ชม. ${minutes} นาที` : `${hours} ชม.`;
  }
}
